import React from 'react';
import { Tab } from '@components/atoms/Tab';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { Button } from '@components/atoms/Button';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { EmptyData } from '@components/molecules/EmptyData';
import { Employee } from '@models/employee.model';
import { UserOrganizationRoleStatus } from '@models/user.model';
import { Card } from '@components/atoms/Card';
import { Avatar } from '@components/atoms/Avatar';
import { Badge } from '@components/atoms/Badge';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

interface CrewMembersProps {
  employees: Employee[];
  onManageMembers: () => void;
  onRemoveMember?: (employeeId: string) => void;
  onSelectMember?: (employee: Employee) => void;
}

export const CrewMembers: React.FC<CrewMembersProps> = ({
  employees,
  onManageMembers,
  onRemoveMember,
  onSelectMember,
}) => {
  return (
    <>
      <div className="flex gap-4 mt-6 mb-4 items-end">
        <Tab
          tabs={[
            {
              id: 'crewMembers',
              name: 'Crew Members',
              leftIcon: <GroupsOutlinedIcon className="w-4 h-4" />,
              count: employees?.length || 0,
            },
          ]}
          activeTab=""
          onTabChange={() => {}}
        />

        <Button
          variant="tertiary"
          onClick={onManageMembers}
          leftIcon={<ManageAccountsOutlinedIcon className="w-4 h-4" />}>
          Manage
        </Button>
      </div>

      {employees.length === 0 && (
        <div className="h-32">
          <EmptyData description="No Crew Members Added" />
        </div>
      )}

      <div className="mt-4 pb-6">
        {employees.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {employees.map((employee) => {
              const isActive =
                employee.user?.organizationRoleStatus !==
                UserOrganizationRoleStatus.DISABLED;
              return (
                <Card
                  key={employee.employeeId}
                  onClick={
                    onSelectMember ? () => onSelectMember(employee) : undefined
                  }
                  className={`min-h-36 flex flex-col justify-between w-full ${
                    onSelectMember
                      ? 'cursor-pointer hover:shadow-md transition-shadow'
                      : ''
                  }`}>
                  <div className="flex justify-between items-start">
                    <Avatar
                      size="lg"
                      src={employee.user?.avatar}
                      text={employee.user?.firstName}
                    />
                    <div className="flex items-center gap-2">
                      {isActive && (
                        <Badge size="sm" variant="success">
                          Active
                        </Badge>
                      )}
                      {onRemoveMember && (
                        <div
                          className="flex items-center justify-center w-4 h-4 rounded-full p-3 hover:bg-gray-100 dark:hover:bg-gray-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveMember(employee.employeeId as string);
                          }}>
                          <CloseOutlinedIcon
                            className="text-gray-500 dark:text-gray-300 cursor-pointer"
                            sx={{ fontSize: '20px' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 min-w-0">
                    <div className="flex gap-1">
                      <p className="text-sm text-gray-900 dark:text-gray-100 capitalize truncate">
                        {employee.user?.firstName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-300 capitalize truncate">
                        {employee.user?.lastName}
                      </p>
                    </div>
                    {employee.user?.email && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {employee.user.email}
                      </p>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
